import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/app/Header";
import BookOnPitchup from "../components/app/BookOnPitchup";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Heart, Star, Loader2, Calendar } from "lucide-react";

export default function Favorites() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/favorites");
      setSites(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const remove = async (id) => {
    await api.delete(`/favorites/${id}`);
    setSites((prev) => prev.filter((s) => s.campsite_id !== id));
  };

  const saveDates = async (id, trip_start, trip_end) => {
    try {
      await api.patch(`/favorites/${id}`, { trip_start, trip_end });
      setSites((prev) => prev.map((s) => s.campsite_id === id ? { ...s, trip_start, trip_end } : s));
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="favorites-page">
      <Header active="favorites" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="eyebrow mb-3">Your shortlist</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight mb-3">Saved Campsites</h1>
        <p className="text-sm text-[#555] mb-10 max-w-xl">Set a trip date to get a weather heads-up 48 hours before you head out.</p>

        {loading ? (
          <div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : !sites.length ? (
          <div className="p-10 border hairline bg-white text-center">
            <p className="text-[#555] mb-4">Nothing saved yet.</p>
            <Link to="/browse" className="btn-primary inline-block">Find campsites</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sites.map((s, i) => (
              <FavCard key={s.campsite_id} site={s} i={i}
                onRemove={() => remove(s.campsite_id)}
                onSaveDates={(a, b) => saveDates(s.campsite_id, a, b)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FavCard({ site, i, onRemove, onSaveDates }) {
  const [start, setStart] = useState(site.trip_start?.slice(0, 10) || "");
  const [end, setEnd] = useState(site.trip_end?.slice(0, 10) || "");

  return (
    <div className="border hairline bg-white flex flex-col" data-testid={`fav-card-${i}`}>
      <img src={site.image} alt={site.name} className="h-44 w-full object-cover" />
      <div className="p-5 flex-1 flex flex-col">
        <div className="eyebrow mb-1">{site.region}</div>
        <Link to={`/campsites/${site.campsite_id}`} className="font-display text-xl leading-tight mb-3 hover:text-[#ff4500]">{site.name}</Link>

        <div className="flex items-center gap-4 text-xs mono">
          <span><Star className="w-3 h-3 inline fill-[#ff4500] text-[#ff4500] mr-1" />{(site.rating ?? 0).toFixed(1)}</span>
          <span>{typeof site.price_per_night === "number" ? `From £${site.price_per_night.toFixed(2)}/nt` : "See on Pitchup"}</span>
        </div>

        <div className="mt-4 p-3 bg-[#fafafa] border hairline" data-testid={`fav-dates-${i}`}>
          <div className="eyebrow mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Trip Dates</div>
          <div className="flex gap-2">
            <input type="date" className="cc-input !p-1.5 text-xs" value={start}
              onChange={(e) => setStart(e.target.value)} data-testid={`fav-start-${i}`} />
            <input type="date" className="cc-input !p-1.5 text-xs" value={end}
              onChange={(e) => setEnd(e.target.value)} data-testid={`fav-end-${i}`} />
          </div>
          <button className="mt-2 text-[11px] mono underline text-[#ff4500]"
            onClick={() => onSaveDates(start || null, end || null)}
            data-testid={`fav-save-dates-${i}`}>
            {(start || end) ? "Save trip dates" : "Clear trip dates"}
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <BookOnPitchup
            campsiteId={site.campsite_id}
            pitchupListed={site.pitchup_listed}
            pitchupUrl={site.pitchup_url || null}
            pitchupUrlOk={site.pitchup_url_ok}
            campsiteName={site.name}
            campsiteRegion={site.region}
            websiteUrl={site.website || null}
            phone={site.phone || null}
            tripStart={start || null}
            tripEnd={end || null}
            variant="primary"
            size="sm"
            label="Find on Pitchup"
            testId={`fav-book-${i}`}
            fullWidth
            showHint
          />
          <button onClick={onRemove}
            className="w-full btn-secondary !px-3 !py-2 text-[11px] flex items-center justify-center gap-1.5" data-testid={`fav-remove-${i}`}>
            <Heart className="w-3 h-3 fill-[#ff4500] text-[#ff4500]" /> Remove from shortlist
          </button>
        </div>
      </div>
    </div>
  );
}
